import React, { useState, useRef } from "react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { Column, Row, Text, Icon } from "@amzn/meridian";
import Textarea from "@amzn/meridian/textarea";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import cameraTokens from "@amzn/meridian-tokens/base/icon/camera";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-large";
import { InspectionResult } from "src/services/inspectionService";
import { useAppDispatch } from "src/store/store";
import { updateInspectionResult } from "src/store/inspectionResultsSlice";
import ManualCaptureMultipleStep from "../steps/ManualCaptureMultipleStep";
import "./overrideCompliancePage.scss";

const OverrideCompliancePage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
    const [comment, setComment] = useState<string>("");
    const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
    const [evidencePreviews, setEvidencePreviews] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCamera, setShowCamera] = useState(false);

    const state = location.state as {
        result: InspectionResult;
        questionText: string;
        objectCheck?: { object_name: string; is_compliant: boolean; ai_reason: string };
        questionMode?: string;
    } | null;

    if (!state) {
        return <Navigate to={`/configurationDrivenInspections/${inspectionId}/review`} replace />;
    }

    const { result, questionText, objectCheck } = state;

    const requiresEvidence = state.questionMode !== "manual";

    const getGuidanceMessage = (): string => {
        if (objectCheck) {
            const displayName = objectCheck.object_name.replace(/\s*sign\s*$/i, "").trim();
            return `Take multiple photos to cover ${displayName} signage at the cage entrance`;
        }
        return "Take photos as evidence for the override";
    };

    const handleCameraSubmit = (files: File[]) => {
        setEvidenceFiles((prev) => [...prev, ...files]);
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                setEvidencePreviews((prev) => [...prev, event.target?.result as string]);
            };
            reader.readAsDataURL(file);
        });
        setShowCamera(false);
    };

    const handleAttach = () => {
        if (fileInputRef.current) {
            fileInputRef.current.removeAttribute("capture");
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setEvidenceFiles((prev) => [...prev, ...files]);

        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                setEvidencePreviews((prev) => [...prev, event.target?.result as string]);
            };
            reader.readAsDataURL(file);
        });

        e.target.value = "";
    };

    const handleSubmit = async () => {
        if (!selectedAnswer) return;
        setIsSubmitting(true);

        const newIsCompliant = selectedAnswer === "Yes";
        const newConfirmedResult = newIsCompliant ? "YES" : "NO";

        const formData = new FormData();
        formData.append("result_id", result.resultId);
        formData.append("ai_result", result.aiResult || "");
        formData.append("confirmed_result", newConfirmedResult);
        formData.append("is_compliant", String(newIsCompliant));
        formData.append("is_inspector_override", "true");
        // The inspector's comment, when provided, becomes the recorded override reason.
        // Falls back to the sentinel value when no comment was entered.
        const trimmedComment = comment.trim();
        formData.append("override_reason", trimmedComment !== "" ? trimmedComment : "manual_override");

        if (objectCheck) {
            formData.append("object_name", objectCheck.object_name);
        }

        evidenceFiles.forEach((file) => {
            formData.append("files", file);
        });

        try {
            await dispatch(updateInspectionResult({
                inspection_id: inspectionId!,
                question_id: result.questionId,
                formData,
            })).unwrap();

            navigate(-1);
        } catch (error) {
            console.error("Failed to submit override:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const canSubmit = selectedAnswer !== null && (!requiresEvidence || evidenceFiles.length > 0);

    if (showCamera) {
        return (
            <ManualCaptureMultipleStep
                humanText={getGuidanceMessage()}
                activeQuestionIds={[]}
                onComplete={() => setShowCamera(false)}
                onSubmitFiles={handleCameraSubmit}
            />
        );
    }

    return (
        <div className="override-compliance-page pwa-scrollable">
            <Column spacingInset="500" spacing="500">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="override-compliance-page__back"
                        onClick={() => navigate(-1)}
                        role="button"
                    >
                        <Icon tokens={chevronLeftLargeTokens} color="success" />
                    </div>
                    <Text type="b300" color="primary">
                        Override Compliance
                    </Text>
                </Row>

                <p className="override-compliance-page__question-text">
                    {questionText.split(/\(\*\*|\*\*\)/).map((segment, i) => {
                        if (i % 2 === 1) {
                            return (
                                <strong key={i} className="override-compliance-page__question-bold">
                                    ({segment})
                                </strong>
                            );
                        }
                        return <span key={i}>{segment}</span>;
                    })}
                </p>

                <Row spacing="300">
                    <button
                        className={`override-compliance-page__answer-btn ${selectedAnswer === "Yes" ? "override-compliance-page__answer-btn--selected" : ""}`}
                        onClick={() => setSelectedAnswer("Yes")}
                    >
                        Yes
                    </button>
                    <button
                        className={`override-compliance-page__answer-btn ${selectedAnswer === "No" ? "override-compliance-page__answer-btn--selected" : ""}`}
                        onClick={() => setSelectedAnswer("No")}
                    >
                        No
                    </button>
                </Row>

                {selectedAnswer && requiresEvidence && (
                    <>
                        <Text type="h100" color="primary">Add evidence</Text>
                        <Row spacing="300" alignmentHorizontal="center">
                            <div
                                className="override-compliance-page__evidence-box"
                                onClick={() => setShowCamera(true)}
                                role="button"
                            >
                                <Icon tokens={cameraTokens} color="success" />
                                <Text type="b100" color="secondary">Capture</Text>
                            </div>
                            <Text type="b100" color="secondary">OR</Text>
                            <div
                                className="override-compliance-page__evidence-box"
                                onClick={handleAttach}
                                role="button"
                            >
                                <Icon tokens={uploadTokens} color="success" />
                                <Text type="b100" color="secondary">Attach</Text>
                            </div>
                        </Row>

                        {evidencePreviews.length > 0 && (
                            <Row spacing="300" wrap="down">
                                {evidencePreviews.map((preview, idx) => (
                                    <img
                                        key={idx}
                                        src={preview}
                                        alt={`Evidence ${idx + 1}`}
                                        className="override-compliance-page__thumbnail"
                                    />
                                ))}
                            </Row>
                        )}
                    </>
                )}

                {selectedAnswer && (
                    <Column spacing="100">
                        <Text type="h100" color="primary">Comment (Optional)</Text>
                        <Textarea
                            value={comment}
                            onChange={(value: string) => setComment(value)}
                            placeholder="Add an optional comment for this override..."
                            rows={2}
                        />
                    </Column>
                )}

            </Column>

            <button
                className={`override-compliance-page__submit-btn ${canSubmit ? "override-compliance-page__submit-btn--enabled" : ""}`}
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
            >
                {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
            </button>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                style={{ display: "none" }}
            />
        </div>
    );
};

export default OverrideCompliancePage;
